import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import {
  Shield, Plus, FileText, Truck, Download, Edit, Trash2, AlertTriangle,
  User, MapPin, Clock, ChevronRight, Check, X, RefreshCw, Eye,
  Scale, Target, Users, Car, Building, Phone, Pen, Pencil, CheckCircle2
} from 'lucide-react';

// Status colors
const STATUS_COLORS = {
  DRAFT: 'bg-slate-100 text-slate-700 border-slate-200',
  ACTIVE: 'bg-green-100 text-green-700 border-green-200',
  EXECUTED: 'bg-blue-100 text-blue-700 border-blue-200',
  CANCELLED: 'bg-red-100 text-red-700 border-red-200'
};

const RISK_COLORS = {
  HIGH: 'bg-red-500 text-white',
  MEDIUM: 'bg-orange-500 text-white',
  LOW: 'bg-green-500 text-white'
};

const CASE_CLASSIFICATIONS = [
  { value: 'BAIL_BOND_APPREHENSION', label: 'Bail Bond Apprehension' },
  { value: 'FUGITIVE_RECOVERY', label: 'Fugitive Recovery' },
  { value: 'WARRANT_EXECUTION', label: 'Warrant Execution' },
  { value: 'LOCATE_ONLY', label: 'Locate Only' }
];

const AUTHORITY_LEVELS = [
  { value: 'FULL_CUSTODY', label: 'Full Custody Authorization' },
  { value: 'DETAIN_TRANSFER', label: 'Detain & Transfer' },
  { value: 'LOCATE_REPORT', label: 'Locate & Report Only' }
];

// Signature Pad Component
const SignaturePad = ({ onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#0F172A';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSave = () => {
    if (!signerName.trim()) {
      toast({ title: 'Error', description: 'Please enter signer name', variant: 'destructive' });
      return;
    }
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    onSave({ signatureData, signerName, signerTitle });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-slate-700">Signer Name *</Label>
        <Input 
          value={signerName} 
          onChange={e => setSignerName(e.target.value)}
          className="bg-white border-slate-300"
          placeholder="Full name"
        />
      </div>
      <div>
        <Label className="text-slate-700">Title</Label>
        <Input 
          value={signerTitle} 
          onChange={e => setSignerTitle(e.target.value)}
          className="bg-white border-slate-300"
          placeholder="e.g., Lead Investigator"
        />
      </div>
      <div>
        <Label className="text-slate-700">Signature</Label>
        <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            width={400}
            height={150}
            className="w-full touch-none cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        <Button variant="ghost" size="sm" onClick={clearCanvas} className="mt-1 text-slate-500">
          Clear
        </Button>
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSave} className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white">
          Save Signature
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-slate-300">
          Cancel
        </Button>
      </div>
    </div>
  );
};

// ATA Form Component
const ATAForm = ({ caseData, subjects, onSubmit, initialData = null, onCancel }) => {
  const [activeSection, setActiveSection] = useState('bondInfo');
  const [form, setForm] = useState(initialData || {
    bail_bond_company: '',
    bail_bond_address: '',
    bail_bond_city: '',
    bail_bond_state: 'Texas',
    bail_bond_zip: '',
    court_jurisdiction: '',
    court_agency: '',
    defendant_id: '',
    defendant_name: '',
    defendant_dob: '',
    cid_number: '',
    court_case_number: '',
    cause_number: '',
    charge: '',
    bond_amount: '',
    case_classification: 'BAIL_BOND_APPREHENSION',
    authority_level: 'FULL_CUSTODY',
    violence_history: false,
    armed_indicator: false,
    flight_risk: false,
    gang_affiliation: false,
    mental_health_risk: false,
    risk_notes: '',
    last_known_address: '',
    last_seen_location: '',
    known_associates: '',
    known_vehicles: '',
    known_locations: '',
    surveillance_notes: '',
    operation_type: '',
    operation_date: '',
    operation_time: '',
    lead_investigator: '',
    assigned_team: '',
    staging_location: '',
    entry_method: '',
    backup_plan: '',
    primary_objective: '',
    secondary_objective: '',
    detainment_instructions: '',
    search_instructions: '',
    evidence_handling: '',
    authorized_force_level: '',
    force_restrictions: '',
    legal_compliance_notes: ''
  });

  // Auto-fill from selected subject
  const handleSubjectSelect = (subjectId) => {
    if (subjectId === '__none__') {
      setForm(prev => ({ ...prev, defendant_id: '' }));
      return;
    }
    const subject = subjects?.find(s => s.id === subjectId);
    if (subject) {
      setForm(prev => ({
        ...prev,
        defendant_id: subjectId,
        defendant_name: subject.name || '',
        defendant_dob: subject.date_of_birth || '',
        last_known_address: subject.last_known_address || subject.addresses?.[0] || ''
      }));
    }
  };

  const sections = [
    { id: 'bondInfo', label: 'Bail Bond Info', icon: Building },
    { id: 'defendant', label: 'Defendant', icon: User },
    { id: 'classification', label: 'Classification', icon: Scale },
    { id: 'risk', label: 'Risk Profile', icon: AlertTriangle },
    { id: 'intelligence', label: 'Intelligence', icon: Eye },
    { id: 'operations', label: 'Operations', icon: Target },
    { id: 'apprehension', label: 'Apprehension', icon: Shield },
    { id: 'force', label: 'Use of Force', icon: Scale }
  ];

  return (
    <div className="flex flex-col h-[80vh]">
      {/* Section Navigation */}
      <div className="flex gap-1 overflow-x-auto pb-2 border-b border-slate-200 mb-4">
        {sections.map(section => (
          <Button
            key={section.id}
            variant={activeSection === section.id ? 'default' : 'ghost'}
            size="sm"
            className={activeSection === section.id ? 'bg-[#0F172A] text-white' : 'text-slate-600'}
            onClick={() => setActiveSection(section.id)}
          >
            <section.icon className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">{section.label}</span>
          </Button>
        ))}
      </div>

      {/* Form Content */}
      <div className="flex-1 overflow-y-auto px-1 space-y-4">
        {activeSection === 'bondInfo' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Building className="w-5 h-5 text-[#0F172A]" />
              Bail Bond Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Company Name</Label>
                <Input value={form.bail_bond_company} onChange={e => setForm({...form, bail_bond_company: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Address</Label>
                <Input value={form.bail_bond_address} onChange={e => setForm({...form, bail_bond_address: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">City</Label>
                <Input value={form.bail_bond_city} onChange={e => setForm({...form, bail_bond_city: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">State</Label>
                <Input value={form.bail_bond_state} onChange={e => setForm({...form, bail_bond_state: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">ZIP Code</Label>
                <Input value={form.bail_bond_zip} onChange={e => setForm({...form, bail_bond_zip: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Court Jurisdiction</Label>
                <Input value={form.court_jurisdiction} onChange={e => setForm({...form, court_jurisdiction: e.target.value})}
                  className="bg-white border-slate-300" placeholder="State / County" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-700">Court/Agency</Label>
                <Input value={form.court_agency} onChange={e => setForm({...form, court_agency: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'defendant' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <User className="w-5 h-5 text-[#0F172A]" />
              Defendant Information
            </h3>
            {subjects && subjects.length > 0 && (
              <div>
                <Label className="text-slate-700">Link to Existing Subject</Label>
                <Select value={form.defendant_id || '__none__'} onValueChange={handleSubjectSelect}>
                  <SelectTrigger className="bg-white border-slate-300">
                    <SelectValue placeholder="Select subject (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {subjects.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Full Name *</Label>
                <Input value={form.defendant_name} onChange={e => setForm({...form, defendant_name: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Date of Birth</Label>
                <Input type="date" value={form.defendant_dob} onChange={e => setForm({...form, defendant_dob: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">CID Number</Label>
                <Input value={form.cid_number} onChange={e => setForm({...form, cid_number: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Court Case Number</Label>
                <Input value={form.court_case_number} onChange={e => setForm({...form, court_case_number: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Cause Number</Label>
                <Input value={form.cause_number} onChange={e => setForm({...form, cause_number: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Bond Amount</Label>
                <Input value={form.bond_amount} onChange={e => setForm({...form, bond_amount: e.target.value})}
                  className="bg-white border-slate-300" placeholder="$" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-700">Charge</Label>
                <Textarea value={form.charge} onChange={e => setForm({...form, charge: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'classification' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#0F172A]" />
              Case Classification & Authority
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Case Classification</Label>
                <Select value={form.case_classification} onValueChange={v => setForm({...form, case_classification: v})}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CASE_CLASSIFICATIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Authority Level</Label>
                <Select value={form.authority_level} onValueChange={v => setForm({...form, authority_level: v})}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUTHORITY_LEVELS.map(a => (
                      <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'risk' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#0F172A]" />
              Risk Profile
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'violence_history', label: 'Violence History' },
                { key: 'armed_indicator', label: 'Armed Indicator' },
                { key: 'flight_risk', label: 'Flight Risk' },
                { key: 'gang_affiliation', label: 'Gang Affiliation' },
                { key: 'mental_health_risk', label: 'Mental Health Risk' }
              ].map(indicator => (
                <div key={indicator.key} className="flex items-center space-x-2">
                  <Checkbox 
                    id={indicator.key}
                    checked={form[indicator.key]}
                    onCheckedChange={checked => setForm({...form, [indicator.key]: checked})}
                  />
                  <Label htmlFor={indicator.key} className="text-slate-700 cursor-pointer">
                    {indicator.label}
                  </Label>
                </div>
              ))}
            </div>
            <div>
              <Label className="text-slate-700">Risk Notes</Label>
              <Textarea value={form.risk_notes} onChange={e => setForm({...form, risk_notes: e.target.value})}
                className="bg-white border-slate-300" rows={3} />
            </div>
          </div>
        )}

        {activeSection === 'intelligence' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#0F172A]" />
              Intelligence Summary
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Last Known Address</Label>
                <Input value={form.last_known_address} onChange={e => setForm({...form, last_known_address: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Last Seen Location</Label>
                <Input value={form.last_seen_location} onChange={e => setForm({...form, last_seen_location: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Known Associates</Label>
                <Textarea value={form.known_associates} onChange={e => setForm({...form, known_associates: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Known Vehicles</Label>
                <Textarea value={form.known_vehicles} onChange={e => setForm({...form, known_vehicles: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Known Locations</Label>
                <Textarea value={form.known_locations} onChange={e => setForm({...form, known_locations: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Surveillance Notes</Label>
                <Textarea value={form.surveillance_notes} onChange={e => setForm({...form, surveillance_notes: e.target.value})}
                  className="bg-white border-slate-300" rows={3} />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'operations' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Target className="w-5 h-5 text-[#0F172A]" />
              Operational Plan
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Operation Type</Label>
                <Input value={form.operation_type} onChange={e => setForm({...form, operation_type: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Operation Date</Label>
                <Input type="date" value={form.operation_date} onChange={e => setForm({...form, operation_date: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Operation Time</Label>
                <Input type="time" value={form.operation_time} onChange={e => setForm({...form, operation_time: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Lead Investigator</Label>
                <Input value={form.lead_investigator} onChange={e => setForm({...form, lead_investigator: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Assigned Team</Label>
                <Input value={form.assigned_team} onChange={e => setForm({...form, assigned_team: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Staging Location</Label>
                <Input value={form.staging_location} onChange={e => setForm({...form, staging_location: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Entry Method</Label>
                <Input value={form.entry_method} onChange={e => setForm({...form, entry_method: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
              <div>
                <Label className="text-slate-700">Backup Plan</Label>
                <Input value={form.backup_plan} onChange={e => setForm({...form, backup_plan: e.target.value})}
                  className="bg-white border-slate-300" />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'apprehension' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#0F172A]" />
              Apprehension Instructions
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Primary Objective</Label>
                <Textarea value={form.primary_objective} onChange={e => setForm({...form, primary_objective: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Secondary Objective</Label>
                <Textarea value={form.secondary_objective} onChange={e => setForm({...form, secondary_objective: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Detainment Instructions</Label>
                <Textarea value={form.detainment_instructions} onChange={e => setForm({...form, detainment_instructions: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Search Instructions</Label>
                <Textarea value={form.search_instructions} onChange={e => setForm({...form, search_instructions: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Evidence Handling</Label>
                <Textarea value={form.evidence_handling} onChange={e => setForm({...form, evidence_handling: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
            </div>
          </div>
        )}

        {activeSection === 'force' && (
          <div className="space-y-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#0F172A]" />
              Use of Force Guidelines
            </h3>
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700">Authorized Force Level</Label>
                <Select value={form.authorized_force_level} onValueChange={v => setForm({...form, authorized_force_level: v})}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Select level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VERBAL_COMMANDS">Verbal Commands Only</SelectItem>
                    <SelectItem value="HANDS_ON">Hands-On Control</SelectItem>
                    <SelectItem value="DEFENSIVE_TACTICS">Defensive Tactics</SelectItem>
                    <SelectItem value="INTERMEDIATE_WEAPONS">Intermediate Weapons</SelectItem>
                    <SelectItem value="DEADLY_FORCE">Deadly Force (If Necessary)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-700">Restrictions</Label>
                <Textarea value={form.force_restrictions} onChange={e => setForm({...form, force_restrictions: e.target.value})}
                  className="bg-white border-slate-300" rows={2} />
              </div>
              <div>
                <Label className="text-slate-700">Legal Compliance Notes</Label>
                <Textarea value={form.legal_compliance_notes} onChange={e => setForm({...form, legal_compliance_notes: e.target.value})}
                  className="bg-white border-slate-300" rows={3} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-200 flex gap-3">
        <Button onClick={() => onSubmit(form)} className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white">
          {initialData ? 'Update ATA' : 'Create ATA'}
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-slate-300">Cancel</Button>
      </div>
    </div>
  );
};

// Transport Log Form Component
const TransportLogForm = ({ ata, onSubmit, onCancel }) => {
  const [form, setForm] = useState({
    pickup_location: '',
    pickup_date: '',
    pickup_time: '',
    arresting_investigator: '',
    transport_vehicle: '',
    transport_officers: '',
    transport_start_time: '',
    transport_end_time: '',
    receiving_facility: '',
    facility_address: '',
    receiving_officer_name: '',
    receiving_officer_badge: '',
    defendant_condition: '',
    property_logged: false,
    evidence_transferred: false,
    transfer_notes: ''
  });

  return (
    <div className="flex flex-col max-h-[70vh]">
      <div className="flex-1 overflow-y-auto space-y-4 px-1">
        {/* Pickup Details */}
        <div className="p-4 bg-slate-50 rounded-lg space-y-3">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Pickup Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700 text-sm">Location</Label>
              <Input value={form.pickup_location} onChange={e => setForm({...form, pickup_location: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Date</Label>
              <Input type="date" value={form.pickup_date} onChange={e => setForm({...form, pickup_date: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Time</Label>
              <Input type="time" value={form.pickup_time} onChange={e => setForm({...form, pickup_time: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Arresting Investigator</Label>
              <Input value={form.arresting_investigator} onChange={e => setForm({...form, arresting_investigator: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
          </div>
        </div>

        {/* Transport Details */}
        <div className="p-4 bg-slate-50 rounded-lg space-y-3">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <Truck className="w-4 h-4" /> Transport Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700 text-sm">Vehicle</Label>
              <Input value={form.transport_vehicle} onChange={e => setForm({...form, transport_vehicle: e.target.value})}
                className="bg-white border-slate-300" placeholder="Vehicle make/model/plate" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Officers</Label>
              <Input value={form.transport_officers} onChange={e => setForm({...form, transport_officers: e.target.value})}
                className="bg-white border-slate-300" placeholder="Officer names" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Start Time</Label>
              <Input type="time" value={form.transport_start_time} onChange={e => setForm({...form, transport_start_time: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">End Time</Label>
              <Input type="time" value={form.transport_end_time} onChange={e => setForm({...form, transport_end_time: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
          </div>
        </div>

        {/* Destination */}
        <div className="p-4 bg-slate-50 rounded-lg space-y-3">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <Building className="w-4 h-4" /> Destination
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-700 text-sm">Receiving Facility</Label>
              <Input value={form.receiving_facility} onChange={e => setForm({...form, receiving_facility: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Facility Address</Label>
              <Input value={form.facility_address} onChange={e => setForm({...form, facility_address: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Receiving Officer</Label>
              <Input value={form.receiving_officer_name} onChange={e => setForm({...form, receiving_officer_name: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
            <div>
              <Label className="text-slate-700 text-sm">Badge/ID Number</Label>
              <Input value={form.receiving_officer_badge} onChange={e => setForm({...form, receiving_officer_badge: e.target.value})}
                className="bg-white border-slate-300" />
            </div>
          </div>
        </div>

        {/* Custody Transfer */}
        <div className="p-4 bg-slate-50 rounded-lg space-y-3">
          <h4 className="font-semibold text-slate-800 flex items-center gap-2">
            <Shield className="w-4 h-4" /> Custody Transfer
          </h4>
          <div>
            <Label className="text-slate-700 text-sm">Defendant Condition</Label>
            <Textarea value={form.defendant_condition} onChange={e => setForm({...form, defendant_condition: e.target.value})}
              className="bg-white border-slate-300" rows={2} />
          </div>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox id="property_logged" checked={form.property_logged}
                onCheckedChange={checked => setForm({...form, property_logged: checked})} />
              <Label htmlFor="property_logged" className="text-slate-700 cursor-pointer">Property Logged</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="evidence_transferred" checked={form.evidence_transferred}
                onCheckedChange={checked => setForm({...form, evidence_transferred: checked})} />
              <Label htmlFor="evidence_transferred" className="text-slate-700 cursor-pointer">Evidence Transferred</Label>
            </div>
          </div>
          <div>
            <Label className="text-slate-700 text-sm">Transfer Notes</Label>
            <Textarea value={form.transfer_notes} onChange={e => setForm({...form, transfer_notes: e.target.value})}
              className="bg-white border-slate-300" rows={2} />
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="sticky bottom-0 bg-white pt-4 border-t border-slate-200 flex gap-3">
        <Button onClick={() => onSubmit(form)} className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white">
          Create Transport Log
        </Button>
        <Button variant="outline" onClick={onCancel} className="border-slate-300">Cancel</Button>
      </div>
    </div>
  );
};

// Main Warrant Operations Tab Component
const WarrantOperationsTab = ({ caseId, caseData, subjects, token, isAdmin }) => {
  const [atas, setAtas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAta, setSelectedAta] = useState(null);
  const [editingAta, setEditingAta] = useState(null);
  const [showCreateAta, setShowCreateAta] = useState(false);
  const [showTransportForm, setShowTransportForm] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(null);
  const [signatureType, setSignatureType] = useState('');
  const [signingEntity, setSigningEntity] = useState(null); // 'ata' or transport log id

  const fetchAtas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/ata`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setAtas(await res.json());
      }
    } catch (e) {
      console.error('Fetch ATAs error:', e);
    } finally {
      setLoading(false);
    }
  }, [caseId, token]);

  const fetchAtaDetails = useCallback(async (ataId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${ataId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSelectedAta(await res.json());
      }
    } catch (e) {
      console.error('Fetch ATA details error:', e);
    }
  }, [token]);

  useEffect(() => {
    fetchAtas();
  }, [fetchAtas]);

  const handleCreateAta = async (formData) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${caseId}/ata`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'ATA created successfully' });
        setShowCreateAta(false);
        fetchAtas();
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create ATA');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleUpdateAtaStatus = async (ataId, status) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${ataId}/status?status=${status}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast({ title: 'Success', description: `ATA status updated to ${status}` });
        fetchAtas();
        if (selectedAta?.id === ataId) {
          fetchAtaDetails(ataId);
        }
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteAta = async (ataId) => {
    if (!window.confirm('Delete this ATA? This cannot be undone.')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${ataId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Delete failed (${res.status})`);
      }
      toast({ title: 'ATA deleted' });
      if (selectedAta?.id === ataId) setSelectedAta(null);
      fetchAtas();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleApproveAta = async (ataId) => {
    await handleUpdateAtaStatus(ataId, 'APPROVED');
  };

  const handleEditAta = async (ataId) => {
    // Pull the full ATA into the Create/Edit dialog
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${ataId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to load ATA for editing');
      const full = await res.json();
      setEditingAta(full);
      setShowCreateAta(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleSaveEditAta = async (formData) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${editingAta.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Failed to update ATA');
      }
      toast({ title: 'ATA updated' });
      setEditingAta(null);
      setShowCreateAta(false);
      fetchAtas();
      if (selectedAta?.id === editingAta.id) fetchAtaDetails(editingAta.id);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleCreateTransportLog = async (formData) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ata/${selectedAta.id}/transport-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Transport log created' });
        setShowTransportForm(false);
        fetchAtaDetails(selectedAta.id);
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create transport log');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleAddSignature = async (sigData) => {
    const endpoint = signingEntity === 'ata' 
      ? `${BACKEND_URL}/api/ata/${selectedAta.id}/signatures`
      : `${BACKEND_URL}/api/transport-logs/${signingEntity}/signatures`;
    
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          signature_type: signatureType,
          signature_data: sigData.signatureData,
          signer_name: sigData.signerName,
          signer_title: sigData.signerTitle
        })
      });
      if (res.ok) {
        toast({ title: 'Success', description: 'Signature added' });
        setShowSignaturePad(false);
        fetchAtaDetails(selectedAta.id);
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const handleExportPdf = async (includeTransport = false) => {
    try {
      const url = `${BACKEND_URL}/api/ata/${selectedAta.id}/export-pdf?include_transport=${includeTransport}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `ATA_${selectedAta.id}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        toast({ title: 'Success', description: 'PDF downloaded' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to download PDF', variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading warrant operations...</div>;
  }

  return (
    <div className="space-y-6" data-testid="warrant-operations-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#0F172A]" />
            Warrant Operations
          </h2>
          <p className="text-sm text-slate-500">Authorization to Arrest documents and Transport Logs</p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => { setEditingAta(null); setShowCreateAta(true); }}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            data-testid="create-ata-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create ATA
          </Button>
        )}
      </div>

      {/* Iter131d — full-page ATA form (previously a modal popup). */}
      {isAdmin && showCreateAta && (
        <div className="fixed inset-0 z-40 bg-white overflow-y-auto" data-testid="create-ata-fullpage">
          <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {editingAta ? 'Edit Authorization to Arrest' : 'Create Authorization to Arrest'}
              </h2>
              <p className="text-xs text-slate-500 mt-1">Complete every tab to finalize the ATA. All fields marked * are required.</p>
            </div>
            <Button variant="ghost" onClick={() => { setShowCreateAta(false); setEditingAta(null); }}
                    className="text-slate-500" data-testid="close-ata-fullpage">
              Close
            </Button>
          </div>
          <div className="max-w-6xl mx-auto p-6">
            <ATAForm
              caseData={caseData}
              subjects={subjects}
              initialData={editingAta}
              onSubmit={editingAta ? handleSaveEditAta : handleCreateAta}
              onCancel={() => { setShowCreateAta(false); setEditingAta(null); }}
            />
          </div>
        </div>
      )}

      {/* ATA List or Detail View */}
      {selectedAta ? (
        <div className="space-y-4">
          {/* Back button */}
          <Button variant="ghost" onClick={() => setSelectedAta(null)} className="text-slate-600">
            <ChevronRight className="w-4 h-4 mr-1 rotate-180" /> Back to List
          </Button>

          {/* ATA Detail Card */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{selectedAta.id}</span>
                    <Badge className={`${STATUS_COLORS[selectedAta.status]} text-xs border`}>
                      {selectedAta.status}
                    </Badge>
                    <Badge className={`${RISK_COLORS[selectedAta.risk_level]} text-xs`}>
                      {selectedAta.risk_level} RISK
                    </Badge>
                  </div>
                  <CardTitle className="text-slate-800">{selectedAta.defendant_name}</CardTitle>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select onValueChange={(v) => handleUpdateAtaStatus(selectedAta.id, v)}>
                    <SelectTrigger className="w-[140px] bg-white border-slate-300">
                      <SelectValue placeholder="Change Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="ACTIVE">Active</SelectItem>
                      <SelectItem value="EXECUTED">Executed</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={() => handleExportPdf(false)} className="border-slate-300">
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExportPdf(true)} className="border-slate-300">
                    <Download className="w-4 h-4 mr-1" /> PDF + Logs
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Defendant Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Charge</p>
                  <p className="text-sm text-slate-800 font-medium">{selectedAta.charge || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Bond Amount</p>
                  <p className="text-sm text-slate-800 font-medium">{selectedAta.bond_amount || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Court Case #</p>
                  <p className="text-sm text-slate-800 font-medium">{selectedAta.court_case_number || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Classification</p>
                  <p className="text-sm text-slate-800 font-medium">
                    {CASE_CLASSIFICATIONS.find(c => c.value === selectedAta.case_classification)?.label || selectedAta.case_classification}
                  </p>
                </div>
              </div>

              {/* Iter140 — ESignaturePanel removed from every case per owner directive. */}

              {/* Signatures */}
              <div className="border border-slate-200 rounded-lg p-4" data-testid="ata-signatures-panel">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Pen className="w-4 h-4" /> Warrant Signatures
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(() => {
                      const sigs = selectedAta.signatures || [];
                      const hasAdmin = sigs.some(s => (s.signature_type || '').toLowerCase() === 'admin');
                      const hasClient = sigs.some(s => (s.signature_type || '').toLowerCase() === 'client');
                      const approved = !!selectedAta.approved_at;
                      const signWith = async (kind) => {
                        const name = window.prompt(
                          kind === 'admin' ? 'Type the admin/field-command signer name'
                          : kind === 'client' ? 'Type the client signer name'
                          : ''
                        );
                        if (!name || !name.trim()) return;
                        const res = await fetch(`${BACKEND_URL}/api/ata/${selectedAta.id}/signatures`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({
                            signature_type: kind,
                            signature_data: '',
                            signer_name: name.trim(),
                            signer_title: kind === 'admin' ? 'Field Command' : kind === 'client' ? 'Client / Bondsman' : '',
                          }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) return alert(data?.detail || 'Failed to sign');
                        await handleSelectAta(selectedAta.id);
                      };
                      const approveAta = async () => {
                        const res = await fetch(`${BACKEND_URL}/api/ata/${selectedAta.id}/signatures`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ signature_type: 'approve', signer_name: 'Admin Approval' }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) return alert(data?.detail || 'Failed to approve');
                        await handleSelectAta(selectedAta.id);
                      };
                      return (
                        <>
                          {!hasAdmin && (
                            <Button size="sm" onClick={() => signWith('admin')}
                                    className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                                    data-testid="ata-admin-sign-btn">
                              <Pen className="w-3.5 h-3.5 mr-1" /> Admin E-Sign
                            </Button>
                          )}
                          {hasAdmin && !hasClient && (
                            <Button size="sm" onClick={() => signWith('client')}
                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="ata-client-sign-btn">
                              <Pen className="w-3.5 h-3.5 mr-1" /> Send / Record Client E-Sign
                            </Button>
                          )}
                          {hasAdmin && hasClient && !approved && (
                            <Button size="sm" onClick={approveAta}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    data-testid="ata-approve-btn">
                              <Check className="w-3.5 h-3.5 mr-1" /> Approve Warrant
                            </Button>
                          )}
                          {approved && (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300">
                              AUTHORIZED — COMPLETE
                            </Badge>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {selectedAta.signatures?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAta.signatures.map(sig => (
                      <div key={sig.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded" data-testid={`ata-sig-${sig.signature_type}`}>
                        <Check className="w-4 h-4 text-green-600" />
                        <div>
                          <p className="text-sm text-slate-800 font-medium">
                            {sig.signer_name} <span className="text-slate-500 text-xs uppercase">({sig.signature_type})</span>
                          </p>
                          <p className="text-xs text-slate-500">
                            {sig.signer_title || '—'} · {new Date(sig.signed_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    No signatures yet. Admin must sign first, then send to the client for their signature.
                  </p>
                )}
              </div>

              {/* Transport Logs */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Transport Logs
                  </h4>
                  <Dialog open={showTransportForm} onOpenChange={setShowTransportForm}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="border-slate-300" data-testid="add-transport-log-btn">
                        <Plus className="w-4 h-4 mr-1" /> Add Log
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-hidden">
                      <DialogHeader>
                        <DialogTitle>Create Transport Log</DialogTitle>
                      </DialogHeader>
                      <TransportLogForm 
                        ata={selectedAta}
                        onSubmit={handleCreateTransportLog}
                        onCancel={() => setShowTransportForm(false)}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
                {selectedAta.transport_logs?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedAta.transport_logs.map(log => (
                      <Card key={log.id} className="bg-slate-50 border-slate-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className="font-mono text-xs text-slate-500">{log.id}</span>
                              <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">{log.status}</Badge>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSigningEntity(log.id);
                                setShowSignaturePad('transport');
                              }}
                            >
                              <Pen className="w-3 h-3 mr-1" /> Sign
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-slate-500">Pickup:</span> {log.pickup_location}
                            </div>
                            <div>
                              <span className="text-slate-500">Destination:</span> {log.receiving_facility}
                            </div>
                            <div>
                              <span className="text-slate-500">Date:</span> {log.pickup_date} {log.pickup_time}
                            </div>
                            <div>
                              <span className="text-slate-500">Officer:</span> {log.arresting_investigator}
                            </div>
                          </div>
                          {log.signatures?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <p className="text-xs text-slate-500 mb-1">Signatures:</p>
                              <div className="flex gap-2">
                                {log.signatures.map(sig => (
                                  <Badge key={sig.id} className="bg-green-100 text-green-700 text-xs">
                                    {sig.signer_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No transport logs yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Transport Signature Dialog */}
          <Dialog open={showSignaturePad === 'transport'} onOpenChange={(open) => setShowSignaturePad(open ? 'transport' : null)}>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle>Add Transport Signature</DialogTitle>
              </DialogHeader>
              <div className="mb-4">
                <Label>Signature Type</Label>
                <Select value={signatureType} onValueChange={setSignatureType}>
                  <SelectTrigger className="bg-white border-slate-300"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transport_officer">Transport Officer</SelectItem>
                    <SelectItem value="receiving_officer">Receiving Officer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {signatureType && (
                <SignaturePad onSave={handleAddSignature} onCancel={() => setShowSignaturePad(null)} />
              )}
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        /* ATA List View */
        <div>
          {atas.length === 0 ? (
            <Card className="bg-white border-slate-200">
              <CardContent className="p-12 text-center">
                <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-800 mb-2">No ATAs Found</h3>
                <p className="text-slate-500 mb-4">Create your first Authorization to Arrest document</p>
                {isAdmin && (
                  <Button onClick={() => setShowCreateAta(true)} className="bg-[#0F172A] hover:bg-[#1E293B] text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create ATA
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {atas.map(ata => (
                <Card 
                  key={ata.id} 
                  className="bg-white border-slate-200 hover:border-[#0F172A]/30 hover:shadow-md transition-all"
                  data-testid={`ata-card-${ata.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3 cursor-pointer" onClick={() => fetchAtaDetails(ata.id)}>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{ata.id}</span>
                          <Badge className={`${STATUS_COLORS[ata.status]} text-xs border`}>
                            {ata.status}
                          </Badge>
                        </div>
                        <h3 className="text-slate-800 font-semibold">{ata.defendant_name}</h3>
                      </div>
                      <Badge className={`${RISK_COLORS[ata.risk_level]} text-xs`}>
                        {ata.risk_level}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 border-t border-slate-100 pt-3">
                      <div className="flex items-center gap-1">
                        <Scale className="w-3 h-3 text-slate-400" />
                        {CASE_CLASSIFICATIONS.find(c => c.value === ata.case_classification)?.label?.split(' ')[0] || ata.case_classification}
                      </div>
                      <div className="flex items-center gap-1">
                        <Truck className="w-3 h-3 text-slate-400" />
                        {ata.transport_log_count || 0} Transport Logs
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
                      <button
                        onClick={(e) => { e.stopPropagation(); fetchAtaDetails(ata.id); }}
                        className="text-xs text-[#0F172A] font-medium flex items-center gap-1 hover:underline"
                        data-testid={`ata-view-${ata.id}`}
                      >
                        View Details <ChevronRight className="w-3 h-3" />
                      </button>
                      <div className="flex gap-1">
                        {isAdmin && ata.status === 'DRAFT' && (
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleApproveAta(ata.id); }}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white h-7 px-2 text-xs"
                            data-testid={`ata-approve-${ata.id}`}
                            title="Approve ATA"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {isAdmin && ['DRAFT', 'APPROVED'].includes(ata.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleEditAta(ata.id); }}
                            className="border-slate-300 text-slate-700 hover:bg-slate-100 h-7 px-2 text-xs"
                            data-testid={`ata-edit-${ata.id}`}
                            title="Edit ATA"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDeleteAta(ata.id); }}
                            className="border-red-200 text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                            data-testid={`ata-delete-${ata.id}`}
                            title="Delete ATA"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarrantOperationsTab;
